import styled, { css } from 'styled-components';
import { Input, Select } from '@shared/ui/Input';
import { fleetLeading, fleetType } from '@entities/fleet';
import { TextButton } from '@widgets/FleetRegistry/ui';
import { DataTable, NARROW, cellLink } from '../ui';

/** Колонок в таблице — строка подробностей занимает их все. */
export const COLUMNS = 5;

/** `position: relative` — точка отсчёта для скрытой живой области. */
export const PanelRoot = styled.div`
  position: relative;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
`;

export const Search = styled.div`
  position: relative;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 480px;

  & > svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(3)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.secondary};
    pointer-events: none;
  }

  @container chronum (max-width: ${NARROW}) {
    flex: 1 1 100%;
    max-width: none;
  }
`;

export const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing(9)};
`;

/** Фильтр-переключатель: «Только без категории · 3». */
export const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: 32px;
  padding: ${({ theme }) => `0 ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.status.warning}73;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.small};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.warning};
  }

  &:hover,
  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.colors.status.warning}1F;
  }

  &[aria-pressed='true'] {
    font-weight: 600;
  }
`;

export const Spacer = styled.span`
  flex: 1 1 auto;

  @container chronum (max-width: ${NARROW}) {
    display: none;
  }
`;

export const Summary = styled.p`
  margin: ${({ theme }) => `${theme.spacing(4)} 0 ${theme.spacing(1)}`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  text-wrap: pretty;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const Table = styled(DataTable)`
  margin-top: ${({ theme }) => theme.spacing(3)};

  th.c-name {
    min-width: 220px;
    font-weight: 400;
  }

  th.c-name > .name {
    display: block;
    font-size: ${fleetType.base};
    font-weight: 500;
    line-height: ${fleetLeading.tight};
    text-wrap: pretty;
    overflow-wrap: anywhere;
  }

  th.c-name .comment {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  td.c-companies {
    min-width: 140px;
    max-width: 260px;
    overflow-wrap: anywhere;
  }

  td.c-category {
    width: 240px;
  }

  td.c-group {
    min-width: 160px;
  }

  td.c-group a {
    ${cellLink}
  }

  tr.row:hover > *,
  tr.row:focus-within > * {
    background: ${({ theme }) => theme.colors.surface};
  }

  tr.row[data-open='true'] > * {
    background: ${({ theme }) => theme.colors.surface};
    border-bottom-color: transparent;
  }

  tr.details > td {
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(5)}`};
    background: ${({ theme }) => theme.colors.surface};
  }

  @container chronum (max-width: ${NARROW}) {
    &,
    & > tbody {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    & > tbody > tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      row-gap: ${({ theme }) => theme.spacing(2)};
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > tbody > tr > th,
    & > tbody > tr > td {
      display: block;
      width: auto;
      min-width: 0;
      max-width: none;
      padding: 0;
      border: none;
    }

    tr.row:hover > *,
    tr.row:focus-within > *,
    tr.row[data-open='true'] > * {
      background: none;
    }

    & > tbody > tr.row[data-open='true'] {
      border-bottom: none;
      padding-bottom: 0;
    }

    & > tbody > tr.details {
      display: block;
      padding: ${({ theme }) => `${theme.spacing(2)} 0 ${theme.spacing(4)}`};
    }

    & > tbody > tr.details > td {
      padding: ${({ theme }) => theme.spacing(3)};
      border-radius: ${({ theme }) => theme.radius.md};
    }

    .narrow-only {
      display: block;
    }
  }
`;

export const CategorySelect = styled(Select)<{ $unset: boolean }>`
  min-width: 0;
  height: 36px;
  padding-top: ${({ theme }) => theme.spacing(1.5)};
  padding-bottom: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.body};

  ${({ $unset, theme }) =>
    $unset &&
    css`
      border-color: ${theme.colors.status.warning}99;
      color: ${theme.colors.text.secondary};
    `}
`;

export const SaveLine = styled.span<{ $error?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme, $error }) => ($error ? theme.colors.status.danger : theme.colors.text.secondary)};

  & > svg {
    flex: none;
    color: ${({ theme }) => theme.colors.status.success};
  }
`;

export const NarrowLabel = styled.span`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Приглушённая ссылка строки: в списке их много, акцент — только под курсором. */
export const DetailsToggle = styled(TextButton)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.small};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover:not(:disabled),
  &[aria-expanded='true'] {
    color: ${({ theme }) => theme.colors.primary};
  }

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
  }

  &[aria-expanded='true'] > svg {
    transform: rotate(180deg);
  }
`;

export const NothingFound = styled.p`
  margin: ${({ theme }) => `${theme.spacing(8)} 0`};
  font-size: ${fleetType.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(2.5)};

  & > .done {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & > .done > svg {
    color: ${({ theme }) => theme.colors.status.success};
  }
`;

export const Updated = styled.p`
  margin: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;
